import { useEffect, useRef, useState } from "react";
import UNIT_CONSTANTS from "../../../constants/unitConstants";
import Select from "react-select";
import { showError, showSuccess } from "../../../utils/toastUtils";
import ProductAPI from "../../../api/inventory/ProductApi";
import useUsernameInput from "../../../hooks/useUsernameInput";
import ImageUploader from "../../../components/partials/ImageUploader";
import categoryAPI from "../../../api/categoryApi";
import brandAPI from "../../../api/brandApi";
import slugify from "slugify";
import { handleApiValidationError } from "../../../utils/apiErrorHandler";

const Step1BasicInfo = ({ nextStep, productId }) => {
  useUsernameInput();

  const [categoryTree, setCategoryTree] = useState([]);
  const [visibleOptions, setVisibleOptions] = useState([]);
  const [selectedPath, setSelectedPath] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const originalThumbnailRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    skuCode: "",
    slug: "",
    unit: "",
    hsnCode: "",
    productType: "Single",
    gst: "0",
    // publishDate: "",
    minimumOrderQuantity: "1",
    maximumOrderQuantity: "10",
    thumbnail: "",
    categoryId: "",
    productId: "",
    brandId: "",
    isTrending: false,
    customFields: [],
    isFeatured: false,
  });

  const [skuCodeEffect, setSkuCodeEffect] = useState(false);
  // const today = new Date().toISOString().split("T")[0];
  const [errors, setErrors] = useState({});

  const [brandOptions, setBrandOptions] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);

  const fetchStep1BasicDetails = async (req, res) => {
    try {
      const res = await ProductAPI.fetchStep1BasicDetails(productId);

      if (res.code === 200) {
        const result = res.data;
        sessionStorage.setItem("productType", result.productType);
        setEditMode(true);

        originalThumbnailRef.current = result.thumbnail;

        setFormData({
          title: result.title,
          skuCode: result.skuCode,
          slug: result.slug,
          unit: result.unit,
          hsnCode: result.hsnCode,
          productType: result.productType,
          gst: result.gst,
          thumbnail: result.thumbnail,
          categoryId: result.categoryId,
          brandId: result?.brandId?._id,
          // publishDate: result.publishDate,
          isTrending: result.isTrending,
          isFeatured: result.isFeatured,
          customFields: result.customFields,
          maximumOrderQuantity: result.maximumOrderQuantity,
          minimumOrderQuantity: result.minimumOrderQuantity,
        });

        setSelectedBrand({
          value: result?.brandId?._id,
          label: result?.brandId?.name,
        });
      }
    } catch (err) {
      showError(err.message);
    }
  };

  const fetchBrandList = async () => {
    try {
      const res = await brandAPI.getListforDropdown();
      if (res.code === 200) {
        const brandList = [
          { value: "", label: "Select Brand" },
          ...res.data.map((b) => ({
            value: b?.id,
            label: b.name,
          })),
        ];
        setBrandOptions(brandList);
      }
    } catch (err) {}
  };

  useEffect(() => {
    fetchBrandList();

    categoryAPI.getListforDropdown().then((res) => {
      if (res.code === 200) {
        setCategoryTree(res.data);
        setVisibleOptions(res.data);
      }
    });

    if (productId) {
      fetchStep1BasicDetails();
    }
  }, []);

  const handleSelect = (item) => {
    const newPath = [...selectedPath, item];
    setSelectedPath(newPath);

    if (item.children && item.children.length > 0) {
      // Go deeper
      setVisibleOptions(item.children);
    } else {
      // Leaf selected
      setFormData((prev) => ({
        ...prev,
        categoryId: item._id,
      }));
      setIsOpen(false);
    }
  };

  const handleBrandChange = (option) => {
    setSelectedBrand(option);

    setFormData((prev) => ({
      ...prev,
      brandId: option ? option.value : "",
    }));
  };

  const selectedLabel = selectedPath.map((i) => i.name).join(" → ");

  const flattenCategories = (categories, level = 0, parentPath = "") => {
    let result = [];

    categories.forEach((cat) => {
      const fullPath = parentPath ? `${parentPath} → ${cat.name}` : cat.name;

      result.push({
        label: fullPath,
        value: cat._id,
        slug: cat.slugPath,
        level,
      });

      if (cat.children && cat.children.length > 0) {
        result = result.concat(
          flattenCategories(cat.children, level + 1, fullPath),
        );
      }
    });

    return result;
  };

  const findCategoryPath = (categories, targetId, path = []) => {
    for (const cat of categories) {
      const newPath = [...path, cat];

      if (cat._id === targetId) {
        return newPath;
      }

      if (cat.children && cat.children.length > 0) {
        const result = findCategoryPath(cat.children, targetId, newPath);
        if (result) return result;
      }
    }
    return null;
  };

  useEffect(() => {
    if (!categoryTree.length || !formData.categoryId) return;

    const path = findCategoryPath(categoryTree, formData.categoryId);

    if (path) {
      setSelectedPath(path);
      // leaf hai → parent ke children dikhao
      if (path.length > 1) {
        const parent = path[path.length - 2];
        setVisibleOptions(parent.children || []);
      } else {
        setVisibleOptions(categoryTree);
      }
    }
  }, [categoryTree, formData.categoryId]);

  const validate = () => {
    const skuRegex = /^[A-Z0-9_-]{4,20}$/;

    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }
    if (!formData.hsnCode.trim()) {
      newErrors.hsnCode = "Hsn Code is required";
    }
    if (!formData.productType.trim()) {
      newErrors.productType = "Product Type is required";
    }

    if (!formData.skuCode.trim()) {
      newErrors.skuCode = "Sku Code is required";
    } else if (!skuRegex.test(formData.skuCode)) {
      newErrors.skuCode = "Invalid SKU Code";
    }

    if (!formData.unit) {
      newErrors.unit = "Unit is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const generateSKU = () => {
    setSkuCodeEffect(true);

    const date = new Date();
    const namePart = formData.title.substring(0, 2).toUpperCase();
    const year = date.getFullYear().toString().substr(2, 2);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const random = Math.floor(1000 + Math.random() * 9000);

    setTimeout(function () {
      setFormData((prev) => ({
        ...prev,
        skuCode: `SKU-${namePart}-${year}${month}-${random}`,
        slug: slugify(formData.title, { lower: true }),
      }));

      setSkuCodeEffect(false);
    }, 300);
  };

  const handleImageUploadSuccess = (type, url) => {
    setFormData((prev) => ({
      ...prev,
      [type]: url,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const payload = { ...formData };

      if (payload.thumbnail === originalThumbnailRef.current) {
        delete payload.thumbnail;
      }

      if (productId) {
        payload.productId = productId;
      }

      const res = await ProductAPI.storeBasicInfo(payload);

      if (res.code === 200) {
        showSuccess("Product Saved in Draft Successfully");

        sessionStorage.setItem("product", formData.title);
        sessionStorage.setItem("productType", formData.productType);
        sessionStorage.setItem("productSku", formData.skuCode);

        const result = res.data;
        sessionStorage.setItem("productId", result.productId);
        nextStep(formData);
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    }
  };

  // customization fields

  const addCustomField = () =>
    setFormData((prev) => ({
      ...prev,
      customFields: [
        ...(prev.customFields || []),
        { key: "", label: "", sublabel: "", type: "text", required: false, options: [] },
      ],
    }));

  const removeCustomField = (index) =>
    setFormData((prev) => ({
      ...prev,
      customFields: (prev.customFields || []).filter((_, i) => i !== index),
    }));

  const handleCustomFieldChange = (index, name, value) =>
    setFormData((prev) => {
      const u = [...(prev.customFields || [])];
      u[index] = { ...u[index], [name]: value };
      if (name === "label")
        u[index].key = value
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "");
      if (name === "type" && value !== "select") u[index].options = [];
      return { ...prev, customFields: u };
    });

  const addOption = (fi) =>
    setFormData((prev) => {
      const u = [...prev.customFields];
      u[fi].options = [...u[fi].options, ""];
      return { ...prev, customFields: u };
    });

  const handleOptionChange = (fi, oi, value) =>
    setFormData((prev) => {
      const u = [...prev.customFields];
      const o = [...u[fi].options];
      o[oi] = value;
      u[fi].options = o;
      return { ...prev, customFields: u };
    });

  const removeOption = (fi, oi) =>
    setFormData((prev) => {
      const u = [...prev.customFields];
      u[fi].options = u[fi].options.filter((_, i) => i !== oi);
      return { ...prev, customFields: u };
    });

  return (
    <>
      <div className="card-header d-flex align-items-center justify-content-between">
        <h4>General Settings</h4>
      </div>
      <div className="card-body">
        <form method="post" onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-12">
              <ImageUploader
                type="thumbnail"
                label="Product Thumbnail"
                value={formData.thumbnail}
                onUploadSuccess={handleImageUploadSuccess}
                assetType="products"
              />
            </div>
          </div>
          <div className="row">
            <div className="col-lg-6 col-sm-12">
              <label className="form-label">
                Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                name="title"
                value={formData.title}
                onChange={handleChange}
                onBlur={(e) => (!editMode ? generateSKU(e.target.value) : null)}
              />
              <span className="fs--1">Slug: {formData.slug}</span>
              <div>
                {errors.title && (
                  <span className="errors text-danger">{errors.title}</span>
                )}
              </div>
            </div>
            <div className="col-lg-6 col-sm-12">
              <label className="form-label">
                HSN Code <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                maxLength={9}
                name="hsnCode"
                value={formData.hsnCode}
                onChange={handleChange}
              />
              {errors.hsnCode && (
                <span className="errors text-danger">{errors.hsnCode}</span>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6 col-sm-12">
              <div className="mb-3">
                <label className="form-label">Parent Category</label>
                <div className="input-group">
                  <span className="input-group-text" id="basic-addon1">
                    <i className="fa fa-home"></i>
                  </span>

                  <div className="position-relative" style={{ width: "100%" }}>
                    <div
                      className="form-control"
                      onClick={() => setIsOpen(!isOpen)}
                      style={{ cursor: "pointer" }}
                    >
                      {selectedLabel || "Select Category"}
                    </div>

                    {isOpen && (
                      <div
                        className="border bg-white shadow position-absolute w-100"
                        style={{ zIndex: 10 }}
                      >
                        {visibleOptions.map((item) => (
                          <div
                            key={item._id}
                            onClick={() => handleSelect(item)}
                            style={{
                              padding: "8px 12px",
                              cursor: "pointer",
                              borderBottom: "1px solid #eee",
                            }}
                          >
                            {item.name}
                            {item.children?.length > 0 && " ›"}
                          </div>
                        ))}

                        {selectedPath.length > 0 && (
                          <div
                            onClick={() => {
                              const parentPath = selectedPath.slice(0, -1);
                              setSelectedPath(parentPath);
                              const parent =
                                parentPath.length === 0
                                  ? categoryTree
                                  : parentPath[parentPath.length - 1].children;
                              setVisibleOptions(parent);
                            }}
                            className="p-2 text-primary"
                            style={{ cursor: "pointer" }}
                          >
                            ← Back
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {errors.categoryid && (
                  <span className="errors text-danger">
                    {errors.categoryid}
                  </span>
                )}
              </div>
            </div>

            <div className="col-lg-6 col-sm-12">
              <label className="form-label">
                Brand <span className="text-danger">*</span>
              </label>
              <Select
                options={brandOptions}
                value={selectedBrand}
                onChange={handleBrandChange}
                isSearchable
                placeholder="Select Brand"
              />
              {errors.brandid && (
                <span className="errors text-danger">{errors.brandid}</span>
              )}
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-lg-6 col-sm-12">
              <label className="form-label">
                Product Type <span className="text-danger">*</span>
              </label>
              <select
                className="form-control form-select"
                name="productType"
                value={formData.productType}
                onChange={handleChange}
              >
                <option>Single</option>
                <option>Variant</option>
              </select>
              {errors.productType && (
                <span className="errors text-danger">{errors.productType}</span>
              )}
            </div>
            <div className="col-lg-6 col-sm-12">
              <label className="form-label">
                SKU Code <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                name="skuCode"
                value={formData.skuCode}
                onChange={handleChange}
              />
              {skuCodeEffect && (
                <span className="fs--1 text-primary">Generating...</span>
              )}
              <div>
                {errors.skuCode && (
                  <span className="errors text-danger">{errors.skuCode}</span>
                )}
              </div>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-lg-6 col-sm-12">
              <label className="form-label">
                GST (%) <span className="text-danger">*</span>
              </label>
              <select
                className="form-control form-select"
                name="gst"
                value={formData.gst}
                onChange={handleChange}
              >
                <option value="0">0%</option>
                <option value="3">3%</option>
                <option value="5">5%</option>
                <option value="12">12%</option>
                <option value="18">18%</option>
                <option value="28">28%</option>
              </select>
            </div>
            <div className="col-lg-6 col-sm-12">
              <label className="form-label">
                Unit <span className="text-danger">*</span>
              </label>
              <select
                className="form-control form-select"
                name="unit"
                value={formData.unit}
                onChange={handleChange}
              >
                <option disabled value="">
                  Select Unit
                </option>
                {Object.entries(UNIT_CONSTANTS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
              {errors.unit && (
                <span className="errors text-danger">{errors.unit}</span>
              )}
            </div>
          </div>

          {/* <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Publish Date</label>
              <input
                type="date"
                className="form-control"
                min={today}
                name="publishDate"
                value={formData.publishDate}
                onChange={handleChange}
              />
            </div>
          </div> */}
          <div className="row mb-3">
            <div className="col-md-3">
              <label className="form-label">
                is Trending?<span className="text-danger">*</span>
              </label>

              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="flexSwitchCheckChecked"
                  name="isTrending"
                  checked={formData.isTrending}
                  onChange={handleChange}
                />
                <label
                  className="form-check-label"
                  htmlFor="flexSwitchCheckChecked"
                >
                  Active
                </label>
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label">
                is Featured?<span className="text-danger">*</span>
              </label>

              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="flexSwitchCheckChecked"
                  name="isFeatured"
                  checked={formData.isFeatured}
                  onChange={handleChange}
                />
                <label
                  className="form-check-label"
                  htmlFor="flexSwitchCheckChecked"
                >
                  Active
                </label>
              </div>
            </div>
          </div>

          <hr />
          <h5 className="mb-2">Other Details</h5>

          <div className="row mb-3">
            <div className="col-lg-6 col-sm-12">
              <label className="form-label">
                Minimum Order Quantity <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                min={1}
                placeholder="(e.g., 5)"
                name="minimumOrderQuantity"
                value={formData.minimumOrderQuantity}
                onChange={handleChange}
              />
              {errors.hsnCode && (
                <span className="errors text-danger">{errors.minimumOrderQuantity}</span>
              )}
            </div>

            <div className="col-lg-6 col-sm-12">
              <label className="form-label">
                Maximum Order Quantity <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                min={1}
                placeholder="(e.g., 5)"
                name="maximumOrderQuantity"
                value={formData.maximumOrderQuantity}
                onChange={handleChange}
              />
              {errors.hsnCode && (
                <span className="errors text-danger">{errors.maximumOrderQuantity}</span>
              )}
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-12">
              <label className="form-label">
                Customization Fields (If Needed)
                <span className="text-danger">*</span>
              </label>
              {(formData.customFields || []).map((field, index) => (
                <div key={index} className="mt-2">
                  <div className="row align-items-center">
                    <div className="col-md-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Label (e.g., Message on Cake)"
                        value={field.label}
                        onChange={(e) =>
                          handleCustomFieldChange(
                            index,
                            "label",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                    <div className="col-md-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Description"
                        value={field.sublabel}
                        onChange={(e) =>
                          handleCustomFieldChange(
                            index,
                            "sublabel",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                    <div className="col-md-2">
                      <select
                        className="form-control form-select"
                        value={field.type}
                        onChange={(e) =>
                          handleCustomFieldChange(index, "type", e.target.value)
                        }
                      >
                        <option value="text">Text</option>
                        <option value="number">Number</option>
                        <option value="select">Dropdown (Select)</option>
                        <option value="boolean">Checkbox (Yes/No)</option>
                        <option value="image">Image</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`req-${index}`}
                          checked={field.required}
                          onChange={(e) =>
                            handleCustomFieldChange(
                              index,
                              "required",
                              e.target.checked,
                            )
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`req-${index}`}
                        >
                          Required
                        </label>
                      </div>
                    </div>
                    <div className="col-md-2 text-end">
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => removeCustomField(index)}
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </div>
                  </div>

                  {field?.type === "select" && (
                    <div className="mt-2 ps-2 border-start">
                      <small className="text-muted">Options for dropdown</small>
                      {field?.options.map((opt, oi) => (
                        <div
                          key={oi}
                          className="row g-2 mt-1 align-items-center"
                        >
                          <div className="col-md-6">
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder={`Option ${oi + 1}`}
                              value={opt}
                              onChange={(e) =>
                                handleOptionChange(index, oi, e.target.value)
                              }
                            />
                          </div>
                          <div className="col-md-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeOption(index, oi)}
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary mt-2"
                        onClick={() => addOption(index)}
                      >
                        + Add Option
                      </button>
                    </div>
                  )}
                </div>
              ))}
              <div className="mt-2">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={addCustomField}
                >
                  + Add Customization Field
                </button>
              </div>
            </div>
          </div>

          <button
            type="submit"
            id="submitBtn"
            className="btn btn-primary shadow-none"
          >
            Save & Next
          </button>
        </form>
      </div>
    </>
  );
};

export default Step1BasicInfo;
